import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import './shell.css';
import './motion.css';
import SideNav from './SideNav';
import TopBar from './TopBar';
import BottomTabs from './BottomTabs';
import CommandPalette from './CommandPalette';

/**
 * AppShell — the persistent three-pane frame (nav / work area / context rail).
 * Views render in the main area and may set rail content via useShellUI().
 */

const ShellUIContext = createContext(null);

export const useShellUI = () => {
  const context = useContext(ShellUIContext);
  if (!context) {
    throw new Error('useShellUI must be used within AppShell');
  }
  return context;
};

/**
 * ShellRail — declarative rail content. Render it anywhere inside the shell
 * and its children appear in the right context rail (cleared on unmount).
 * Renders nothing in place.
 */
export const ShellRail = ({ children }) => {
  const { setRail } = useShellUI();
  useEffect(() => {
    setRail(children);
    return () => setRail(null);
  }, [children, setRail]);
  return null;
};

/**
 * ShellTopBarActions — declarative topbar content. Render it anywhere inside
 * the shell and its children appear in the topbar's action cluster (between
 * the league chip and ⌘K), cleared on unmount. Renders nothing in place.
 */
export const ShellTopBarActions = ({ children }) => {
  const { setTopbarActions } = useShellUI();
  useEffect(() => {
    setTopbarActions(children);
    return () => setTopbarActions(null);
  }, [children, setTopbarActions]);
  return null;
};

const TITLES = [
  ['/home', 'Home'],
  ['/curve', 'The Curve'],
  ['/taco-stand', 'The Taco Stand'],
  ['/simulate', 'Simulate'],
  ['/intel', 'Manager Intel'],
  ['/manager-intel', 'Manager Intel'],
  ['/trade-packages', 'Trade Builder'],
  ['/rate-my-trade', 'Rate My Trade'],
  ['/marketplaces', 'Marketplaces'],
  ['/orphan-marketplace', 'Marketplaces'],
  ['/startup-marketplace', 'Marketplaces'],
  ['/review', 'Expert Review'],
  ['/analyst', 'Analyst Studio'],
];

const NAV_COLLAPSED_KEY = 'ft_nav_collapsed';
const RAIL_COLLAPSED_KEY = 'ft_rail_collapsed';

const AppShell = ({ children }) => {
  const location = useLocation();
  const [navCollapsed, setNavCollapsed] = useState(() => localStorage.getItem(NAV_COLLAPSED_KEY) === '1');
  const [railCollapsed, setRailCollapsed] = useState(() => localStorage.getItem(RAIL_COLLAPSED_KEY) === '1');
  const [railContent, setRailContent] = useState(null);
  const [topbarActions, setTopbarActions] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimer = useRef(null);

  const toast = useCallback((msg) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 2600);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Keep installed (standalone) apps fresh: iOS resumes PWAs from a frozen
  // page and never reloads them, so a deploy strands users on old bundles
  // indefinitely. On launch and on every return-to-foreground, compare the
  // running main bundle against what the server is serving; if they differ,
  // reload. index.html ships with max-age=0, so the fetch always sees truth.
  useEffect(() => {
    const RELOADED_AT_KEY = 'ft_bundle_reloaded_at';
    const currentSrc = () => {
      const script = document.querySelector('script[src*="/static/js/main."]');
      return script ? script.getAttribute('src') : null;
    };
    const check = async () => {
      const mine = currentSrc();
      if (!mine) return; // dev server — no hashed bundle
      try {
        const html = await fetch('/', { cache: 'no-store' }).then((r) => r.text());
        const latest = html.match(/static\/js\/main\.[a-f0-9]+\.js/);
        if (!latest || mine.includes(latest[0])) return;
        // Guard against reload loops if an edge cache lags behind.
        const lastReload = Number(sessionStorage.getItem(RELOADED_AT_KEY) || 0);
        if (Date.now() - lastReload < 60000) return;
        sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
        window.location.reload();
      } catch (e) {
        /* offline — try again next foreground */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // No launch-time check: a cold load already fetched fresh HTML
    // (max-age=0), and reloading mid-boot risks a white screen on a slow
    // connection. Only the resume-from-frozen path can be stale.
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const toggleNav = useCallback(() => {
    setNavCollapsed((prev) => {
      localStorage.setItem(NAV_COLLAPSED_KEY, prev ? '0' : '1');
      return !prev;
    });
  }, []);

  const toggleRail = useCallback(() => {
    setRailCollapsed((prev) => {
      localStorage.setItem(RAIL_COLLAPSED_KEY, prev ? '0' : '1');
      return !prev;
    });
  }, []);

  const openPalette = useCallback(() => setPaletteOpen(true), []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
      if (e.key === 'Escape') setPaletteOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Auto-collapse nav in arena room (full-focus mode)
  const prevNavCollapsed = useRef(null);
  useEffect(() => {
    const isArenaRoom = location.pathname.startsWith('/taco-stand/room/');
    if (isArenaRoom) {
      prevNavCollapsed.current = navCollapsed;
      setNavCollapsed(true);
    } else if (prevNavCollapsed.current !== null) {
      setNavCollapsed(prevNavCollapsed.current);
      prevNavCollapsed.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-open the rail on The Curve — it carries your record and every line
  // you've drawn. Restores the user's preference on leave.
  const prevRailCollapsed = useRef(null);
  useEffect(() => {
    const isCurve = location.pathname.startsWith('/curve');
    if (isCurve && railCollapsed) {
      prevRailCollapsed.current = true;
      setRailCollapsed(false);
    } else if (!isCurve && prevRailCollapsed.current !== null) {
      setRailCollapsed(prevRailCollapsed.current);
      prevRailCollapsed.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const title = useMemo(() => {
    const match = TITLES.find(
      ([path]) => location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
    return match ? match[1] : 'Fantasy Taco';
  }, [location.pathname]);

  // Sheet closes whenever rail content changes surface (navigation).
  useEffect(() => { setSheetOpen(false); }, [location.pathname]);

  const value = useMemo(
    () => ({ setRail: setRailContent, setTopbarActions, toast, openPalette }),
    [toast, openPalette]
  );

  return (
    <ShellUIContext.Provider value={value}>
      <div className={`ft-app${navCollapsed ? ' navc' : ''}${railCollapsed ? ' railc' : ''}${sheetOpen ? ' msheet' : ''}`}>
        <SideNav collapsed={navCollapsed} onToggleCollapse={toggleNav} />
        <div className="ft-stage">
          <TopBar
            title={title}
            actions={topbarActions}
            hasRail={!!railContent}
            onToggleRail={toggleRail}
            onToggleSheet={() => setSheetOpen((open) => !open)}
            onOpenPalette={openPalette}
            toast={toast}
          />
          <main className="ft-main">{children}</main>
        </div>
        {sheetOpen ? <div className="ft-sheetscrim" onClick={() => setSheetOpen(false)} /> : null}
        <aside className="ft-rail" style={railContent ? undefined : { width: 0, borderLeft: 'none' }}>
          {railContent ? <div className="ft-railpanel">{railContent}</div> : null}
        </aside>
        <BottomTabs />
        {paletteOpen && (
          <CommandPalette onClose={() => setPaletteOpen(false)} toast={toast} />
        )}
        {toastMsg && <div className="ft-toast show">{toastMsg}</div>}
      </div>
    </ShellUIContext.Provider>
  );
};

export default AppShell;
